import { ChevronsUpDown } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@components/ui/popover';
import {
    ActivityFilterItem,
    ActivityFilterOption
} from '../utils/activityFilter.utils';
import {
    buildAvailableMap,
    buildSelectionsFromKeys,
    buildSelectedKeys,
    getActivityFilterSummary,
    groupOptionsByCategory
} from '../utils/activityFilter.ui.utils';

interface ActivityFilterProps {
    selectedActivities: ActivityFilterItem[];
    onActivityChange: (activities: ActivityFilterItem[]) => void;
    availableOptions: ActivityFilterOption[];
    isLoading: boolean;
    hasError: boolean;
}

export function ActivityFilter({
    selectedActivities,
    onActivityChange,
    availableOptions,
    isLoading,
    hasError
}: ActivityFilterProps) {
    const [open, setOpen] = useState(false);

    const availableMap = useMemo(
        () => buildAvailableMap(availableOptions),
        [availableOptions]
    );

    const selectedKeys = useMemo(
        () => buildSelectedKeys(selectedActivities),
        [selectedActivities]
    );

    const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

    const toggleKey = (key: string) => {
        const nextKeys = selectedKeySet.has(key)
            ? selectedKeys.filter((k) => k !== key)
            : [...selectedKeys, key];
        onActivityChange(buildSelectionsFromKeys(nextKeys, availableMap));
    };

    const groupedOptions = useMemo(
        () => groupOptionsByCategory(availableOptions),
        [availableOptions]
    );

    const summary = getActivityFilterSummary({
        isLoading,
        hasError,
        availableOptions,
        selectedActivities
    });

    return (
        <div className="flex max-w-md flex-col gap-1.5">
            <p className="text-sm font-semibold">Filter Activities</p>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between font-normal"
                    >
                        <span className="truncate">{summary}</span>
                        <ChevronsUpDown className="opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="max-h-[400px] w-[350px] overflow-y-auto p-2"
                    align="start"
                >
                    {Object.entries(groupedOptions).map(
                        ([categoryName, activities]) =>
                            activities.length === 0 ? null : (
                                <div key={categoryName} className="mb-2">
                                    <p className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                        {categoryName} ({activities.length})
                                    </p>
                                    {activities.map((activity) => (
                                        <label
                                            key={activity.key}
                                            className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                                        >
                                            <Checkbox
                                                checked={selectedKeySet.has(
                                                    activity.key
                                                )}
                                                onCheckedChange={() =>
                                                    toggleKey(activity.key)
                                                }
                                            />
                                            {activity.label}
                                        </label>
                                    ))}
                                </div>
                            )
                    )}
                </PopoverContent>
            </Popover>
            {!isLoading &&
                !hasError &&
                selectedActivities.length === 0 &&
                availableOptions.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                        All activities are shown. Select any to narrow results.
                    </span>
                )}
            {!isLoading && !hasError && availableOptions.length === 0 && (
                <span className="text-xs text-muted-foreground">
                    No activities found in the selected dates
                </span>
            )}
            {isLoading && (
                <span className="text-xs text-muted-foreground">
                    Loading activities from selected dates
                </span>
            )}
            {hasError && (
                <span className="text-xs text-destructive">
                    Unable to load activities. Try again.
                </span>
            )}
        </div>
    );
}
